<script setup lang="ts">
import { type PropType, computed } from 'vue'
import { TYPE_IMG, type LifeFileWithRelationDTO } from '@/composables/memorize/memorize'

import { Swiper, SwiperSlide } from 'swiper/vue'
import { Autoplay } from 'swiper/modules'
// Import Swiper styles
import 'swiper/css'
import 'swiper/css/pagination'
import 'swiper/css/navigation'
import { MEDIA_IMG, MEDIA_VIDEO } from '@/composables/media/media'
import { useRouter } from 'vue-router'
import { titleBar } from '@/composables/menu/title'
import { minifyString } from '@/utils/util'
import { isSetRealName } from '@/composables/user/users'
const router = useRouter()

const emit = defineEmits(['showSetRealNameDialog'])
const props = defineProps({
  name: {
    type: String,
    default: ''
  },
  serviceId: {
    type: String,
    default: ''
  },
  count: {
    type: Number,
    default: 0
  },
  photoList: {
    type: Array as PropType<LifeFileWithRelationDTO[]>,
    default: () => []
  },
  canManage: {
    type: Boolean,
    default: true,
    required: true
  }
})

const aspectRatio = 3 / 4

const imgList = computed(() => {
  return props.photoList.filter((item) => item.file?.type === MEDIA_IMG || item.file?.type === MEDIA_VIDEO)
})

function onAddAlbum() {
  if (!isSetRealName.value) {
    emit('showSetRealNameDialog')
    return
  }
  router.push(
    `/uploadAlbumFile?type=${TYPE_IMG}&serviceId=` + props.serviceId + '&name=' + titleBar.value?.life?.name.fullName
  )
}

const autoplayConfig = {
  delay: 5000,
  disableOnInteraction: false
} as any
</script>

<template>
  <div class="w-11/12 md:w-full" flex="~ row" bg-theme-light rounded-3 overflow-hidden p-4 bg-theme-gray>
    <ClientOnly>
      <swiper
        v-if="imgList && imgList.length > 0"
        :slides-per-view="1"
        :spaceBetween="30"
        :loop="imgList.length > 1"
        :modules="[Autoplay]"
        :autoplay="autoplayConfig"
        class="w-7/12"
        grow
        rounded-2
        h-36
        overflow-hidden>
        <swiper-slide w-full v-for="(photo, index) in imgList" :key="index">
          <RatioSafeBlock
            :ratio="aspectRatio"
            cursor-pointer
            min-h-20
            relative
            @click.stop="$router.push(`/m/${props.serviceId}/album`)">
            <!-- 主图片层 -->
            <img
              ref="imgRef"
              :src="photo.file?.highQuality || photo.file?.middleQuality || photo.file?.url || ''"
              class="relative z-10 scale-105 h-full w-full object-contain brightness-100 transition-all hover:brightness-80" />
          </RatioSafeBlock>
        </swiper-slide>
      </swiper>
    </ClientOnly>
    <div flex="~ col" px-2 h-36 max-w-38>
      <h3 text-theme-main font-bold @click="$router.push(`/m/${props.serviceId}/album`)" cursor-pointer>
        {{ $t('m.slide_ones_album', { name: minifyString(props.name, 12) }) }}
      </h3>
      <p text-theme-gray text-xs mt-1>{{ $t('m.slide_album_count', { count: props.count }) }}</p>
      <button v-if="props.canManage" btn-theme-outline text-xs my-0.5 mt-10 border-1px opacity-70 @click="onAddAlbum">
        {{ $t('m.slide_add_album') }}
      </button>
    </div>
  </div>
</template>

<style scoped>
/* style */
/* 添加模糊过渡动画 */
.blur-layer {
  transition: filter 0.3s ease;
}

/* 优化模糊层性能 */
@media (prefers-reduced-motion: no-preference) {
  .blur-layer {
    will-change: filter;
  }
}

/* 移动端优化 */
@media (max-width: 768px) {
  .blur-layer {
    filter: blur(8px); /* 减小模糊强度 */
  }
}
</style>
